//import { SearchIndexClient, AzureKeyCredential } from '@azure/search-documents'
import { Fragment, useEffect, useState, useRef } from 'react'
import { SESSION_SERVICE } from './services/SessionService'
//import { SECRETS_CONFIG } from './config/SecretsConfig'
import { MetricService } from './services/MetricService'
import { LOCAL_STORAGE } from './services/LocalStorage'
import { APP_CONFIG } from '../src/config/AppConfig'
import { SessionKey } from './constants/SessionKey'
import LabeledInput from './widgets/LabeledInput'
import { MimeTypes } from './constants/MimeTypes'
import { HttpVerb } from './constants/HttpVerb'
import { useNavigate } from 'react-router-dom'
import { Headers } from './constants/Headers'
import { Errors } from './constants/Errors'
import SearchList from './lists/SearchList'
import { Outlet } from 'react-router-dom'
import { isEmpty, merge } from 'lodash'
import { jwtDecode } from 'jwt-decode'
import { upsert, useQueryData } from './hooks/useQueryData.ts'

import {
  TopNavigation,
  Flashbar,
  AppLayout,
  Modal,
  Box,
  SpaceBetween,
  Button,
  Autosuggest
} from '@cloudscape-design/components'
import LabeledTextarea from './widgets/LabeledTextarea'
import Construction from './widgets/Construction'
import { useNotifyStore } from './services/Notify'
import { ClearAllCaches } from './services/Caching.ts'
import { TaskDialog } from './dialogs/TaskDialog.tsx'
import React from 'react'
import HelpIcon from './widgets/HelpIcon.tsx'
import { generalValues } from './widgets/FancyTextEditor.tsx'
import HelpIconVideo from './widgets/HelpIconVideo.tsx'
import { title } from 'process'
import DateTimeInput from './widgets/DateTimeInput.tsx'
import { DateTimePicker } from '@mui/x-date-pickers'
import TextField from './widgets/TextField.tsx'
import dayjs from 'dayjs'
import { LinearProgress, Stack, Typography } from '@mui/material'

export const SetTaskContext = React.createContext((name: string, values: any) => {})

export default function Layout(): JSX.Element {
  const [taskName, setTaskName] = useState("")
  const [taskValues, setTaskValues] = useState({})
  const [progress, setProgress] = useState({message: "", percent: 0, show: false})
  const setTask = (name: string, values: any) => {
    if (name === 'Progress') {
      setProgress(values)
    } else {
      setTaskName(name)
      setTaskValues(values)
    }
  }

  const [search, setSearch] = useState("")
  const [showSearch, setShowSearch] = useState(false)
  const [searchSuggestions, setSearchSuggestions] = useState(JSON.parse(localStorage.getItem("SearchTerms") ?? "[]"))
  const runSearch = (term?: string) => {
    const newSearch: string = term ? term?.trim() : search?.trim()
    if (newSearch.length < 2) return

    setSearch(newSearch)
    setShowSearch(true)

    let newSug = searchSuggestions.filter((s: any, i: number) => (i < 10) && (s.value !== newSearch))
    newSug.unshift({ value: newSearch })
    setSearchSuggestions(newSug)
    localStorage.setItem('SearchTerms', JSON.stringify(newSug)) // "[]")
  }

  const navigate = useNavigate()
  const refreshEffectRan = useRef(false)
  const loginUrl = APP_CONFIG.getLoginUrl()
  const SESSION = SESSION_SERVICE.getCurrentSession()
  const user = SESSION.user
  localStorage.setItem('userName', user.fullName)

  const isLoginExpired = () => {
    // Return false if the identity token is not present
    if (!SESSION.tokens.identityToken) {
      return false;
    }

    // Decode the identity token and convert it into an object
    const json = JSON.parse(JSON.stringify(jwtDecode(SESSION.tokens.identityToken)))
    if (!json) {
      return false;
    }

    // Get the expiration time of the token in seconds
    const expiration = json['exp']
    if (!expiration) {
      return false;
    }

    console.log('JWT tokens expire on %s', new Date(expiration * 1000).toString())

    const now = Math.floor(Date.now() / 1000)
    const expired = !!(expiration < now)

    if (expired) {
      console.log('JWT tokens have expired.')
    }

    return expiration < now
  }

  useEffect(() => {
    async function refreshTokens() {
      if (!refreshEffectRan.current) {
        refreshEffectRan.current = true;

        if (user.signedIn && isLoginExpired()) {
          // Refresh the ID & access tokens from Cognito
          const response = await fetch(APP_CONFIG.getCognitoTokenEndpoint(), {
            method: HttpVerb.POST,
            headers: {
              [Headers.CONTENT_TYPE]: MimeTypes.URL
            },
            body: new URLSearchParams({
              grant_type: 'refresh_token',
              client_id: APP_CONFIG.getCognitoClientId(),
              refresh_token: SESSION.tokens.refreshToken,
            })
          })

          if (!response.ok) {
            // An HTTP status code of 400 means the refresh token expired.
            // There is no way to refresh the ID and access tokens if the
            // refresh token has expired. It essentially means we're now
            // logged out and the user has to login again. If the status
            // code is anything other than 400 we log the error and sign
            // the user out of their login session.
            if (response.status !== 400) {
              const json = await response.json()
              console.log([Errors.REFRESH_LOGIN_TOKENS, json['error']].join(''))
            } else {
              console.log(Errors.REFRESH_TOKENS_EXPIRED)
            }

            SESSION_SERVICE.clearSession()
            navigate('/logout')
      
            return
          }

          // Get the JWT
          const jwt = await response.json()

          // Set the various tokens on the user for easy access
          SESSION.tokens.identityToken = jwt['id_token']
          SESSION.tokens.accessToken = jwt['access_token']

          // Save the current session
          SESSION_SERVICE.setCurrentSession(SESSION)

          // Set the identity token, it's used in the next call (nested deep inside ClientFactory)
          LOCAL_STORAGE.set(SessionKey.IDENTITY_TOKEN, jwt['id_token'])

          console.log('JWT tokens refreshed.')

          isLoginExpired() // for debugging purposes so you can see the new expiration date

          // Emit CloudWatch metric for user login refresh
          await new MetricService().userRefresh(SESSION.user.email)
        }
      }
    }

    refreshTokens()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshEffectRan.current]) // we only want to hit the endpoint once, even in dev mode

  // const notifications = useAppSelector(state => state.notification)
  const [bugVisible, setBugVisible] = useState(false)
  const [clockVisible, setClockVisible] = useState(false)
  // const [emailVisible, setEmailVisible] = useState(false)
  const notifications = useNotifyStore((state) => state.notifications)

  // const redirectPage = LOCAL_STORAGE.getOrDefault(SessionKey.REDIRECT_PAGE, '/')
  //const googleMapsSrc = `https://maps.googleapis.com/maps/api/js?key=${APP_CONFIG.getGoogleMapsApiKey()}&libraries=places&callback=initMap`;

//  if (true) { // user.signedIn) {
  if (user.signedIn) {
    return (
    <SetTaskContext.Provider value={setTask}>
      <Fragment>
        {/* <script async src={googleMapsSrc}></script> */}
        <div id="top-nav">
          {showSearch ? <SearchDialog searchText={search} visible={showSearch} close={() => setShowSearch(false)} /> : <></> }
          {/* <BugDialog visible={bugVisible} close={()=> setBugVisible(false)} currentUser={user.fullName} /> */}
          <ClockDialog visible={clockVisible} close={()=> setClockVisible(false)} currentUser={user.fullName} />
          {/* <EmailDialog visible={emailVisible} close={()=> setEmailVisible(false)} currentUser={user} /> */}
          <TaskDialog taskName={taskName} close={()=> setTaskName("")} values={taskValues} />
          <div id="h">
            {/* <div style={{float: "left", background: "#353F49", height: 70, paddingTop: 10}}>
            <Button variant="link" ariaLabel="Back" onClick={() => window.history.back()}>
              <Icon name="angle-left-double" size="large" />
            </Button>
            </div> */}
           <TopNavigation 
            identity={{
              href: "/",
              logo: { src: "/LegalLoop/Legalloop_Logo_220x80_transparent_v3.png", alt: "LegalLoop" },
            }}
            search={
              <SpaceBetween direction="horizontal" size="xs">
                {progress?.show ? 
                <>
                  <Typography variant="body2" sx={{color: 'white', fontWeight: 'bold'}}>{progress?.message}</Typography>
                  <LinearProgress variant={progress?.percent === -1 ? "indeterminate" : "determinate"} value={progress?.percent} sx={{width: 200, height: 10, borderRadius: 5}} />
                </> :
                <>
                  <Autosuggest
                    value={search}
                    options={searchSuggestions}
                    onChange={event => setSearch(event.detail.value)}
                    onKeyDown={event => {event.detail.key === "Enter" && runSearch()}}
                    onSelect={event => runSearch(event.detail.value)} />
                  {/* <HelpIconVideo videoURL="https://www.youtube.com/watch?v=LXb3EKWsInQ" /> */}
                </>}
              </SpaceBetween>
            }
            utilities={[
              { type: "button", text: "My Overview", href: "/overview" },
              // { type: "button", text: "Intakes", href: "/intakes" },
              { type: "button", text: "Cases", href: "/cases" },
              { type: "button", text: "Management", href: "/management" },
              { type: "button", text: "Contacts", href: "/contacts" },
              {
                type: "button",
                iconName: "keyboard", // transcript
                title: "Quick Case",
                ariaLabel: "Quick Case",
                disableUtilityCollapse: false,
                onClick: () => setTask("QuickCaseTask", {}),
              },
              {
                type: "button",
                iconName: "envelope",
                title: "Send Email",
                ariaLabel: "Send Email",
                disableUtilityCollapse: false,
                onClick: () => setTask("EmailTask", {Case: null, Contact: null, General: generalValues()}
                ),
              },
              // {
              //   type: "button",
              //   iconName: "keyboard", // transcript
              //   title: "User Activity",
              //   ariaLabel: "User Activity",
              //   disableUtilityCollapse: false,
              //   onClick: () => setTask("UserActivityTask", {Case: null, Contact: null, General: generalValues()}),
              // },
              {
                type: "button",
                iconName: "status-pending",
                title: "Work Hours",
                ariaLabel: "Work Hours",
                disableUtilityCollapse: false,
                onClick: () => setClockVisible(true),
              },
              {
                type: "menu-dropdown",
                text: user.fullName,
                iconName: "user-profile",
                items: [
                  {
                    id: "Admin",
                    text: "Admin",
                    href: "/admin",
                  },
                  {
                    id: "Templates",
                    text: "Email Templates",
                    href: "/templates",
                  },
                  {
                    id: "Settings",
                    text: "Settings",
                  },
                  {
                    id: "Flush",
                    text: "Flush caches",
                  },
                  {
                    id: "signout",
                    text: "Sign out",
                    href: "/logout",
                  },
                ],
                onItemClick: (event) => {
                  if (event.detail.id === 'Flush') {
                    if (window.confirm('Are you sure you want to flush all caches?')) {
                      ClearAllCaches();
                    }
                    //navigate('/logout')
                  }
                }
              }
            ]}
          />
          {/* </SpaceBetween> */}
        </div>
        </div>
        <AppLayout navigationHide toolsHide //stickyNotifications
          navigation={<TopNavigation identity={{ title: "test", href: "/" }}></TopNavigation>}
          content={<Outlet />} headerSelector = "#h"
          notifications={<Flashbar items={notifications} stackItems />}
          maxContentWidth={2100} minContentWidth={1000} />
      </Fragment>
      </SetTaskContext.Provider>)
  } else {
    return (
      <div id="signed-out">Click <a href={loginUrl}>here</a> to sign-in to the Legal25 web application.</div>
    )
  }
}

export interface WorkStamp {
  _id: string,
  updatedAt: string,
  createdAt: string,
  updatedBy: string,
  createdBy: string,
  Type: string,
  Date: string,
  TimeMinutes: number,
  Time: string,
  Description: string,
}

export const ClockDialog = ({ visible, close, currentUser }: { visible: boolean, close: () => void, currentUser: string }) => {

  const {data, isLoading, error} = useQueryData('WorkStamp', {Where: [{kind: 'equals', field: 'createdBy', value: currentUser}, {kind: 'equals', field: 'Date', value: dayjs().format('YYYY-MM-DD')}]}, visible)

  const [workStamp, setWorkStamp] = useState<WorkStamp>()

  let nextStampType = "Start Work"
  data?.results?.forEach((ws: WorkStamp) => {
    if (ws.Type === "Start Work") {
      nextStampType = "End Work"
    } else if (ws.Type === "End Work") {
      nextStampType = "Start Work"
    }
  })

  useEffect(() => {
    if (visible) {
      const now = dayjs()
      setWorkStamp({
        _id: "",
        updatedAt: "", createdAt: "",
        updatedBy: currentUser,
        createdBy: currentUser,
        Date: now.format('YYYY-MM-DD'),
        TimeMinutes: now.hour() * 60 + now.minute(),
        Time: now.format('HH:mm'),
        Type: "",
        Description: "",
      })
    }
  }, [visible, currentUser])

  const handleChange = (newValues: Object) => {
    setWorkStamp(merge({}, workStamp, newValues))
  }

  function createWorkStamp(type: string) {
      const newWorkStamp = merge({}, workStamp, { Type: type })
      close()
      upsert('WorkStamp', newWorkStamp)
  }

  return (
    <Modal
      onDismiss={close}
      visible={visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={event => close()}>Cancel</Button>
          </SpaceBetween>
        </Box>
      }
      header="Stamp Work Clock"
    >
      <h2>Work Hours for {workStamp?.createdBy} on {workStamp?.Date}</h2>
      {isLoading && <p>Loading...</p>}
      {error && <p>Error loading work hours</p>}
      {data?.results?.length === 0 && <p>No work hours recorded for today.</p>}
      {data?.results?.map((ws: WorkStamp) => (
        <span key={ws._id}>{ws.Type} at {ws.Time}. {ws.Description}<br/></span>
      ))}
      <hr data-text="New Stamp"/>
      <Stack direction="column" spacing={2} sx={{width: '100%'}}>
        <span style={{fontSize: '16px'}}>New Stamp for <b>{workStamp?.createdBy}</b> at <b>{workStamp?.Time}</b></span>
        <TextField sx={{width: '100%'}} label="Description" value={workStamp?.Description} readOnly={false}
          onChange={(s) => handleChange({ Description: s })} />
        <Stack direction="row" spacing={2} sx={{width: '100%'}}>
          <Button disabled={nextStampType !== "Start Work"} onClick={event => createWorkStamp("Start Work")}>Start Work</Button>
          <Button disabled={nextStampType !== "End Work"} onClick={event => createWorkStamp("End Work")}>End Work</Button>
          <Button onClick={event => createWorkStamp("Break")}>Break</Button>
        </Stack>
      </Stack>
    </Modal>
  )
}

export const BugDialog = ({ visible, close, currentUser }: { visible: boolean, close: () => void, currentUser: string }) => {
  const readOnly = false
  const validating = true
  var valid = true

  const [bug, setBug] = useState<BugRecord>({
    objectId: "", updatedAt: "", createdAt: "",
    updatedBy: currentUser,
    createdBy: currentUser,
    URL: window.location.pathname,
    Description: "",
    Status: "Reported"
  })

  const handleChange = (newValues: Object) => {
    if (!readOnly) setBug(merge({}, bug, newValues))
  }

  function isValid(value: string, label: string): boolean {
    const thisValid: boolean = !isEmpty(value?.trim())
    if (validating) {
      if (!thisValid) valid = false
      return thisValid
    }
    return true
  }

  function createBug() {
    if (valid) {
      const newBug = merge({}, bug, { updatedBy: bug.createdBy })
      close()
      upsert('Bug', newBug)
    }
  }

  return (
    <Modal
      onDismiss={close}
      visible={visible}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={event => close()}>Cancel</Button>
            <Button variant="primary" onClick={event => createBug()}>Ok</Button>
          </SpaceBetween>
        </Box>
      }
      header="Add Bug"
    >
      <LabeledInput label="Filed By" value={bug.createdBy} validate={isValid} readOnly={readOnly}
        onChange={({ detail }) => handleChange({ createdBy: detail.value })} />
      <LabeledInput label="URL / Page" value={bug.URL} validate={isValid} readOnly={readOnly}
        onChange={({ detail }) => handleChange({ URL: detail.value })} />
      <LabeledInput label="Description" value={bug.Description} validate={isValid} readOnly={readOnly}
        onChange={({ detail }) => handleChange({ Description: detail.value })} />
    </Modal>
  )
}

export const SearchDialog = ({ searchText, visible, close }: { searchText: string, visible: boolean, close: () => void }) => {

  return (
    <Modal
      onDismiss={close}
      visible={visible}
      header={<>Search Results <HelpIcon text="<h3>Search Results Help</h3><hr/><p>Search currently only finds records for Cases, Contacts & Intakes. The 'All' filter will help you focus on which kind of record you need to find.</p><p>There is a text input containing your search term directly on this dialog. You can edit your search there, without dismissing the Results Dialog and changing the search text on the App Bar.</p><p>Clicking the circular arrow icon will flush the cache, and reload the search index from the database. This can help with searching for newly created records.</p><p><b>Please note:</b> when you are searching for an Intake, and the filter is set to 'All', you will not see Intakes with a status of 'Case Created'. To search for those records, change the filter to Intake.</p>"/></>}
      size="large"
    >
      <SearchList searchText={searchText} />
    </Modal>
  )
}
